import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Dns as AirscaleIcon, AutoAwesome, CheckCircle as CheckCircleIcon, DeleteOutline as DeleteIcon, Download as DownloadIcon, PersonRemove as PersonRemoveIcon, Tune as TuneIcon, Visibility as VisibilityIcon, } from "@mui/icons-material";
import { Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Tooltip, Typography, } from "@mui/material";
import { DataGrid, } from "@mui/x-data-grid";
import { useMemo, useState } from "react";
import { exportCandidatesToCSV } from "../../utils/csvExport";
import ViewSettingsDialog from "../Lists/ViewSettingsDialog";
const OPEN_TO_WORK_FILTERS = ["true", "false", "unknown", "processing", "not_requested"];
const CandidatesTable = ({ candidates, loading, onRemoveFromList, showListActions = false, showEnrichedColumns = false, selectedRowIds = [], onSelectionChange, onEnrich, enrichingCandidateIds = [], onDeleteCandidates, }) => {
    var _a, _b;
    const [selectedCandidate, setSelectedCandidate] = useState(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [sortModel, setSortModel] = useState([]);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});
    const [columnOrder, setColumnOrder] = useState([]);
    const [viewSettingsOpen, setViewSettingsOpen] = useState(false);
    // Exporting from an Open To Work search is the only thing that sets this now, and it only ever
    // writes "true". Every other value means the same thing — nobody has established the person is
    // ready for a change — including the "false" and the never-cleared "processing" that the
    // retired enrichment left behind on records it never finished.
    const getOpenToWorkStatus = (openToWork) => (openToWork === "true" ? "Ready for Change" : "Unknown");
    const getStatusColor = (status) => (status === "Ready for Change" ? "success" : "default");
    // Base columns (always shown)
    const baseColumns = [
        {
            field: "name",
            headerName: "Name",
            width: 200,
            valueGetter: (params) => { var _a; return params.row.name || ((_a = params.row.externalData) === null || _a === void 0 ? void 0 : _a.name) || "N/A"; },
        },
        {
            field: "firstname",
            headerName: "First Name",
            width: 150,
            valueGetter: (params) => params.row.firstname || "N/A",
        },
        {
            field: "lastname",
            headerName: "Last Name",
            width: 150,
            valueGetter: (params) => params.row.lastname || "N/A",
        },
        {
            field: "headline",
            headerName: "Headline",
            width: 300,
            valueGetter: (params) => { var _a; return params.row.headline || ((_a = params.row.externalData) === null || _a === void 0 ? void 0 : _a.headline) || "N/A"; },
        },
        {
            field: "aiResponse",
            headerName: "AI Response",
            width: 300,
            renderCell: (params) => {
                const isEnriching = enrichingCandidateIds.includes(params.row._id);
                const aiEnrichment = params.row.aiEnrichment;
                // Show loading indicator if currently being enriched
                if (isEnriching && !(aiEnrichment === null || aiEnrichment === void 0 ? void 0 : aiEnrichment.customResponse)) {
                    return (_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 1 } }, { children: [_jsx(CircularProgress, { size: 16 }), _jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "Enriching..." }))] })));
                }
                if (!(aiEnrichment === null || aiEnrichment === void 0 ? void 0 : aiEnrichment.customResponse)) {
                    return (_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "\u2014" })));
                }
                // Truncate long responses for display in table
                const response = aiEnrichment.customResponse;
                const truncated = response.length > 100 ? `${response.substring(0, 100)}...` : response;
                return (_jsx(Tooltip, Object.assign({ title: response, placement: "top" }, { children: _jsxs(Box, Object.assign({ sx: {
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            // Add a subtle highlight animation for recently enriched items
                            animation: isEnriching ? "none" : undefined,
                        } }, { children: [_jsx(AutoAwesome, { fontSize: "small", sx: { color: "secondary.main" } }), _jsx(Typography, Object.assign({ variant: "body2", sx: { overflow: "hidden", textOverflow: "ellipsis" } }, { children: truncated }))] })) })));
            },
        },
        {
            field: "email",
            headerName: "Work Email",
            width: 250,
            renderCell: (params) => {
                const email = params.row.email;
                if (!email) {
                    return (_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "\u2014" })));
                }
                return (_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 0.5 } }, { children: [_jsx(CheckCircleIcon, { fontSize: "small", sx: { color: "success.main" } }), _jsx(Typography, Object.assign({ variant: "body2" }, { children: email })), _jsx(Tooltip, Object.assign({ title: "Enriched via Airscale" }, { children: _jsx(AirscaleIcon, { fontSize: "small", sx: { color: "info.main", ml: 0.5 } }) }))] })));
            },
        },
        {
            field: "personalEmail",
            headerName: "Personal Email",
            width: 250,
            renderCell: (params) => {
                const personalEmail = params.row.personalEmail;
                if (!personalEmail || personalEmail === "Not Found") {
                    return (_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "\u2014" })));
                }
                return (_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 0.5 } }, { children: [_jsx(CheckCircleIcon, { fontSize: "small", sx: { color: "warning.main" } }), _jsx(Typography, Object.assign({ variant: "body2" }, { children: personalEmail })), _jsx(Tooltip, Object.assign({ title: "Enriched via ContactOut" }, { children: _jsx(AirscaleIcon, { fontSize: "small", sx: { color: "warning.main", ml: 0.5 } }) }))] })));
            },
        },
        {
            field: "phone",
            headerName: "Phone",
            width: 200,
            renderCell: (params) => {
                const phone = params.row.phone;
                if (!phone) {
                    return (_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "\u2014" })));
                }
                return (_jsxs(Box, Object.assign({ sx: { display: "flex", alignItems: "center", gap: 0.5 } }, { children: [_jsx(CheckCircleIcon, { fontSize: "small", sx: { color: "success.main" } }), _jsx(Typography, Object.assign({ variant: "body2" }, { children: phone })), _jsx(Tooltip, Object.assign({ title: "Enriched via Airscale" }, { children: _jsx(AirscaleIcon, { fontSize: "small", sx: { color: "info.main", ml: 0.5 } }) }))] })));
            },
        },
    ];
    // Enriched columns (shown only when showEnrichedColumns is true)
    const enrichedColumns = [
        {
            field: "currentCompany",
            headerName: "Current Company",
            width: 200,
            valueGetter: (params) => {
                var _a, _b, _c;
                const positionGroups = params.row.positionGroups;
                if (positionGroups && positionGroups.length > 0) {
                    return positionGroups[0].companyName || "N/A";
                }
                return ((_c = (_b = (_a = params.row.linkedInApiProfile) === null || _a === void 0 ? void 0 : _a.positionGroups) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.companyName) || "N/A";
            },
        },
        {
            field: "currentPosition",
            headerName: "Current Position",
            width: 250,
            valueGetter: (params) => {
                var _a, _b, _c, _d, _e, _f;
                const positionGroups = params.row.positionGroups;
                if (positionGroups && positionGroups.length > 0 && ((_a = positionGroups[0].positions) === null || _a === void 0 ? void 0 : _a.length) > 0) {
                    return positionGroups[0].positions[0].title || "N/A";
                }
                return ((_f = (_e = (_d = (_c = (_b = params.row.linkedInApiProfile) === null || _b === void 0 ? void 0 : _b.positionGroups) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.positions) === null || _e === void 0 ? void 0 : _e[0]) === null || _f === void 0 ? void 0 : _f.title) || "N/A";
            },
        },
        {
            field: "location",
            headerName: "Location",
            width: 200,
            valueGetter: (params) => {
                var _a;
                const location = (_a = params.row.linkedInApiProfile) === null || _a === void 0 ? void 0 : _a.locationName;
                return location || "N/A";
            },
        },
        {
            field: "openToWork",
            headerName: "Ready For Change",
            width: 150,
            renderCell: (params) => {
                const status = getOpenToWorkStatus(params.row.openToWork);
                return _jsx(Chip, { label: status, color: getStatusColor(status), size: "small" });
            },
        },
        {
            field: "emailStatus",
            headerName: "Email Status",
            width: 130,
            renderCell: (params) => {
                const status = params.row.emailStatus;
                if (!(status && params.row.email))
                    return (_jsx(Typography, Object.assign({ variant: "body2", color: "text.secondary" }, { children: "\u2014" })));
                return (_jsx(Chip, { label: status.charAt(0).toUpperCase() + status.slice(1), color: status === "high" || status === "valid" ? "success" : status === "medium" ? "warning" : "default", size: "small", variant: "outlined" }));
            },
        },
        {
            field: "skills",
            headerName: "Skills",
            width: 300,
            valueGetter: (params) => {
                const skills = params.row.skills;
                if (!skills || skills.length === 0)
                    return "N/A";
                return skills.slice(0, 5).join(", ") + (skills.length > 5 ? ` (+${skills.length - 5} more)` : "");
            },
        },
        {
            field: "degreeType",
            headerName: "Degree",
            width: 150,
            valueGetter: (params) => { var _a; return ((_a = params.row.degreeType) === null || _a === void 0 ? void 0 : _a.join(", ")) || "N/A"; },
        },
        {
            field: "fieldOfStudy",
            headerName: "Field of Study",
            width: 200,
            valueGetter: (params) => { var _a; return ((_a = params.row.fieldOfStudy) === null || _a === void 0 ? void 0 : _a.join(", ")) || "N/A"; },
        },
        {
            field: "yearsInCurrentCompany",
            headerName: "Years in Company",
            width: 160,
            valueGetter: (params) => params.row.yearsInCurrentCompany != null ? params.row.yearsInCurrentCompany.toFixed(1) : "N/A",
        },
        {
            field: "yearsInCurrentPosition",
            headerName: "Years in Position",
            width: 160,
            valueGetter: (params) => params.row.yearsInCurrentPosition != null ? params.row.yearsInCurrentPosition.toFixed(1) : "N/A",
        },
        {
            field: "yearOfGraduation",
            headerName: "Graduation Year",
            width: 150,
            valueGetter: (params) => { var _a; return ((_a = params.row.yearOfGraduation) === null || _a === void 0 ? void 0 : _a.join(", ")) || "N/A"; },
        },
        {
            field: "spokenLanguages",
            headerName: "Languages",
            width: 200,
            valueGetter: (params) => { var _a; return ((_a = params.row.spokenLanguages) === null || _a === void 0 ? void 0 : _a.map((l) => l.name).join(", ")) || "N/A"; },
        },
    ];
    // Action columns (always shown)
    const actionColumns = [
        {
            field: "linkedInProfile",
            headerName: "LinkedIn Profile",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (_jsx(Button, Object.assign({ size: "small", variant: "outlined", href: `https://www.linkedin.com/in/${params.row.linkedInId}`, target: "_blank", rel: "noopener noreferrer" }, { children: "LinkedIn Profile" }))),
        },
        {
            field: "actions",
            headerName: "Actions",
            width: 100 + (showListActions ? 60 : 0) + (onDeleteCandidates ? 40 : 0),
            sortable: false,
            filterable: false,
            renderCell: (params) => (_jsxs(Box, Object.assign({ display: "flex", gap: 0.5 }, { children: [_jsx(Tooltip, Object.assign({ title: "View Details" }, { children: _jsx(IconButton, Object.assign({ size: "small", onClick: () => handleViewDetails(params.row) }, { children: _jsx(VisibilityIcon, {}) })) })), showListActions && onRemoveFromList && (_jsx(Tooltip, Object.assign({ title: "Remove from List" }, { children: _jsx(IconButton, Object.assign({ size: "small", color: "error", onClick: () => onRemoveFromList(params.row.linkedInId) }, { children: _jsx(PersonRemoveIcon, {}) })) }))), onDeleteCandidates && (_jsx(Tooltip, Object.assign({ title: "Delete contact" }, { children: _jsx(IconButton, Object.assign({ size: "small", color: "error", onClick: () => onDeleteCandidates([params.row._id]) }, { children: _jsx(DeleteIcon, {}) })) })))] }))),
        },
    ];
    // Combine columns based on showEnrichedColumns
    const availableColumns = showEnrichedColumns
        ? [...baseColumns, ...enrichedColumns, ...actionColumns]
        : [...baseColumns, ...actionColumns];
    const columns = useMemo(() => {
        if (columnOrder.length === 0) {
            return availableColumns;
        }
        return [...availableColumns].sort((a, b) => {
            const indexA = columnOrder.indexOf(a.field);
            const indexB = columnOrder.indexOf(b.field);
            if (indexA !== -1 && indexB !== -1)
                return indexA - indexB;
            if (indexA !== -1)
                return -1;
            if (indexB !== -1)
                return 1;
            return 0;
        });
    }, [availableColumns, columnOrder]);
    const handleViewDetails = (candidate) => {
        setSelectedCandidate(candidate);
        setDetailsOpen(true);
    };
    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedCandidate(null);
    };
    const handlePaginationModelChange = (model) => {
        setPaginationModel(model);
    };
    const getCurrentPageCandidateIds = () => {
        const startIndex = paginationModel.page * paginationModel.pageSize;
        const endIndex = startIndex + paginationModel.pageSize;
        return filteredCandidates.slice(startIndex, endIndex).map((candidate) => candidate._id);
    };
    const handleExportAll = () => {
        const candidatesToExport = filteredCandidates;
        const timestamp = new Date().toISOString().split("T")[0];
        const filename = `candidates-all-${timestamp}.csv`;
        exportCandidatesToCSV(candidatesToExport, filename);
    };
    const handleExportSelected = () => {
        const candidatesToExport = filteredCandidates.filter((candidate) => selectedRowIds.includes(candidate._id));
        const timestamp = new Date().toISOString().split("T")[0];
        const filename = `candidates-selected-${selectedRowIds.length}-${timestamp}.csv`;
        exportCandidatesToCSV(candidatesToExport, filename);
    };
    const filteredCandidates = useMemo(() => {
        return candidates.filter((candidate) => {
            // Apply Ready For Change filter
            // If openToWorkStatus is 'processing', treat it as processing
            // Otherwise use the openToWork value, or 'not_requested' if neither exists
            let filterValue;
            if (candidate.openToWorkStatus === "processing") {
                filterValue = "processing";
            }
            else if (candidate.openToWork) {
                filterValue = candidate.openToWork;
            }
            else {
                filterValue = "not_requested"; // Not requested yet
            }
            if (!OPEN_TO_WORK_FILTERS.includes(filterValue)) {
                return false;
            }
            return true;
        });
    }, [candidates]);
    return (_jsxs(Box, Object.assign({ sx: {
            height: "100%",
            display: "flex",
            flexDirection: "column",
            backgroundColor: "background.paper",
            borderRadius: 1,
        } }, { children: [_jsxs(Box, Object.assign({ sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexShrink: 0,
                    px: 2,
                    pt: 2,
                } }, { children: [_jsxs(Typography, Object.assign({ variant: "h6" }, { children: [filteredCandidates.length, " contacts"] })), _jsxs(Box, Object.assign({ sx: { display: "flex", gap: 1, alignItems: "center" } }, { children: [_jsx(Chip, { label: selectedRowIds.length === 0 || selectedRowIds.length === filteredCandidates.length
                                    ? "Selected: All"
                                    : `Selected: ${selectedRowIds.length}`, color: selectedRowIds.length > 0 && selectedRowIds.length < filteredCandidates.length ? "primary" : "default", variant: "outlined", size: "small", sx: { fontWeight: 600 } }), onDeleteCandidates && selectedRowIds.length > 0 && (_jsxs(Button, Object.assign({ variant: "outlined", color: "error", startIcon: _jsx(DeleteIcon, {}), onClick: () => onDeleteCandidates(selectedRowIds), sx: {
                                    fontWeight: 600,
                                } }, { children: ["Delete (", selectedRowIds.length, ")"] }))), onEnrich && (_jsx(Button, Object.assign({ variant: "contained", color: "primary", startIcon: _jsx(AutoAwesome, {}), onClick: onEnrich, sx: {
                                    fontWeight: 600,
                                } }, { children: "Enrich" }))), _jsx(Button, Object.assign({ variant: "outlined", startIcon: _jsx(TuneIcon, {}), onClick: () => setViewSettingsOpen(true), sx: {
                                    fontWeight: 600,
                                } }, { children: "View Settings" })), _jsx(Button, Object.assign({ variant: "outlined", startIcon: _jsx(DownloadIcon, {}), onClick: () => {
                                    if (selectedRowIds.length === 0 || selectedRowIds.length === filteredCandidates.length) {
                                        handleExportAll();
                                    }
                                    else {
                                        handleExportSelected();
                                    }
                                }, sx: {
                                    fontWeight: 600,
                                } }, { children: "Export" }))] }))] })), _jsx(Box, Object.assign({ sx: {
                    flex: 1,
                    width: "100%",
                    minHeight: 0,
                    px: 2,
                    pb: 2,
                } }, { children: _jsx(DataGrid, { rows: filteredCandidates, columns: columns, loading: loading, getRowId: (row) => row._id, sx: {
                        width: "100%",
                        height: "100%",
                        border: "none",
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: (theme) => (theme.palette.mode === "dark" ? "#1a1d24" : "#f5f5f5"),
                            borderBottom: 1,
                            borderColor: "divider",
                        },
                        "& .MuiDataGrid-cell": {
                            borderBottom: 1,
                            borderColor: "divider",
                        },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: 1,
                            borderColor: "divider",
                        },
                    }, pageSizeOptions: [10, 25, 50, 100], paginationModel: paginationModel, onPaginationModelChange: handlePaginationModelChange, filterModel: filterModel, onFilterModelChange: setFilterModel, sortModel: sortModel, onSortModelChange: setSortModel, columnVisibilityModel: columnVisibilityModel, onColumnVisibilityModelChange: setColumnVisibilityModel, density: "standard", checkboxSelection: true, rowSelectionModel: selectedRowIds, onRowSelectionModelChange: (newSelection) => {
                        const newSelectionArray = newSelection;
                        const currentPageIds = getCurrentPageCandidateIds();
                        // Check if this is a header checkbox click by comparing the selection state
                        const wasAllCurrentPageSelected = currentPageIds.every((id) => selectedRowIds.includes(id));
                        const isAllCurrentPageSelected = currentPageIds.every((id) => newSelectionArray.includes(id));
                        // Calculate changes in current page items
                        const currentPageAdded = newSelectionArray.filter((id) => currentPageIds.includes(id) && !selectedRowIds.includes(id));
                        const currentPageRemoved = selectedRowIds.filter((id) => currentPageIds.includes(id) && !newSelectionArray.includes(id));
                        // Header click detection:
                        // 1. All current page items changed state at once (all selected -> none selected OR none selected -> all selected)
                        // 2. The change affects ALL current page items simultaneously
                        const isHeaderClick = wasAllCurrentPageSelected !== isAllCurrentPageSelected &&
                            (currentPageAdded.length === currentPageIds.length ||
                                currentPageRemoved.length === currentPageIds.length);
                        if (isHeaderClick) {
                            if (isAllCurrentPageSelected) {
                                // Header clicked to select all current page
                                const otherPageSelections = selectedRowIds.filter((id) => !currentPageIds.includes(id));
                                if (onSelectionChange) {
                                    onSelectionChange([...otherPageSelections, ...currentPageIds]);
                                }
                            }
                            else {
                                // Header clicked to deselect all current page
                                if (onSelectionChange) {
                                    onSelectionChange(selectedRowIds.filter((id) => !currentPageIds.includes(id)));
                                }
                            }
                        }
                        else {
                            // Individual row checkbox click - just update the selection normally
                            if (onSelectionChange) {
                                onSelectionChange(newSelectionArray);
                            }
                        }
                    }, disableRowSelectionOnClick: false, autoHeight: false, disableVirtualization: false }) })), _jsxs(Dialog, Object.assign({ open: detailsOpen, onClose: handleCloseDetails, maxWidth: "md", fullWidth: true }, { children: [_jsx(DialogTitle, { children: "Candidate Details" }), _jsx(DialogContent, { children: selectedCandidate && (_jsx(Box, Object.assign({ sx: { mt: 2 } }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 2 }, { children: [_jsxs(Grid, Object.assign({ item: true, xs: 12, sm: 6 }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2", gutterBottom: true }, { children: "Name" })), _jsx(Typography, Object.assign({ variant: "body1" }, { children: ((_a = selectedCandidate.externalData) === null || _a === void 0 ? void 0 : _a.name) || "N/A" }))] })), _jsxs(Grid, Object.assign({ item: true, xs: 12, sm: 6 }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2", gutterBottom: true }, { children: "Headline" })), _jsx(Typography, Object.assign({ variant: "body1" }, { children: ((_b = selectedCandidate.externalData) === null || _b === void 0 ? void 0 : _b.headline) || "N/A" }))] })), process.env.BUILD_TYPE === "exclusive" && (_jsxs(Grid, Object.assign({ item: true, xs: 12, sm: 6 }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2", gutterBottom: true }, { children: "Ready For Change" })), _jsx(Chip, { label: getOpenToWorkStatus(selectedCandidate.openToWork), color: getStatusColor(getOpenToWorkStatus(selectedCandidate.openToWork)) })] }))), _jsxs(Grid, Object.assign({ item: true, xs: 12 }, { children: [_jsx(Typography, Object.assign({ variant: "subtitle2", gutterBottom: true }, { children: "LinkedIn ID" })), _jsx(Typography, Object.assign({ variant: "body1" }, { children: selectedCandidate.linkedInId }))] }))] })) }))) }), _jsx(DialogActions, { children: _jsx(Button, Object.assign({ onClick: handleCloseDetails }, { children: "Close" })) })] })), _jsx(ViewSettingsDialog, { open: viewSettingsOpen, onClose: () => setViewSettingsOpen(false), columns: columns, columnVisibilityModel: columnVisibilityModel, onColumnVisibilityChange: setColumnVisibilityModel, filterModel: filterModel, onFilterModelChange: setFilterModel, columnOrder: columnOrder.length > 0 ? columnOrder : columns.map((c) => c.field), onColumnOrderChange: setColumnOrder })] })));
};
export default CandidatesTable;
